import React from "react";
import s from "../Calendar.css";
import moment from "moment";
import userIcon from "/public/SiteIcons/solar_user-bold.svg";
import { Tooltip, OverlayTrigger } from "react-bootstrap";
import CurrencyConverter from "../../../components/CurrencyConverter/CurrencyConverter";

class CalendarCell extends React.Component {
  findEntry = (entries, condition) => entries.find(condition);

  getDisplayName = (name, width) => {
    if (!name) return "Blocked";
    const avgCharWidth = 10;
    if (width <= 80) {
      return name.length > 4 ? name.slice(0, 4) + "..." : name;
    }
    const maxChars = Math.floor(width / avgCharWidth);
    if (name.length > maxChars) {
      return name.slice(0, Math.max(maxChars - 3, 1)) + "...";
    }
    return name;
  };

  buildGroupedMap = (entries, idKey, nameKey, filterFn) => {
    const grouped = {};
    const result = {};
    entries.forEach((entry) => {
      if (entry[idKey] && entry[nameKey] && (!filterFn || filterFn(entry))) {
        const key = `${entry[idKey]}_${entry[nameKey] || "___EMPTY___"}`;
        if (!grouped[key]) grouped[key] = [];
        grouped[key].push({
          date: moment(entry.blockedDates),
          dayStatus: entry.dayStatus,
        });
      }
    });
    Object.keys(grouped).forEach((key) => {
      const sorted = grouped[key].sort((a, b) => a.date - b.date);
      const start = sorted[0];
      const end = sorted[sorted.length - 1];
      let length = sorted.length;
      if (start.dayStatus === "secondHalf") length -= 0.5;
      if (end.dayStatus === "firstHalf") length -= 0.5;
      result[key] = {
        startDate: start.date.format("YYYY-MM-DD"),
        totalDays: length,
      };
    });
    return result;
  };

  render() {
    const { date, listing, isLast } = this.props;
    const dateStr = date.format("YYYY-MM-DD");
    const basePrice = listing.basePrice;
    const currency = listing.currency;
    const dayEntries = listing.calendarMap?.[dateStr] || [];
    const overlays = [];

    const addOverlay = (condition, color, half, clipPath) => {
      if (condition) overlays.push({ color, half, clipPath });
    };

    const fullClip =
      "polygon(100% 0, 100% 21%, 100% 60%, 100% 100%, 0 100%, 0% 60%, 0 0)";
    const firstHalfClip = "polygon(0 0, 0% 100%, 100% 0)";
    const secondHalfClip = "polygon(100% 0, 0% 100%, 100% 100%)";

    const find = (cond) => this.findEntry(dayEntries, cond);

    // Booking entries
    const bookingFirst = find(
      (d) =>
        d.calendarStatus === "blocked" &&
        d.reservationId &&
        d.reservationGuestName &&
        d.dayStatus === "firstHalf"
    );
    const bookingSecond = find(
      (d) =>
        d.calendarStatus === "blocked" &&
        d.reservationId &&
        d.reservationGuestName &&
        d.dayStatus === "secondHalf"
    );
    const fullBooking = find(
      (d) =>
        d.calendarStatus === "blocked" &&
        d.reservationId &&
        d.reservationGuestName &&
        (!d.dayStatus || d.dayStatus === "full")
    );

    const hasBooking = !!bookingFirst || !!bookingSecond || !!fullBooking;

    // --- Overlay logic fixed ---
    // Booking overlays
    if (fullBooking) {
      addOverlay(true, "#1dd265", "full", fullClip);
    } else {
      addOverlay(!!bookingFirst, "#1dd265", "firstHalf", firstHalfClip);
      addOverlay(!!bookingSecond, "#1dd265", "secondHalf", secondHalfClip);
    }

    // Blocked overlays per half (not skipped if other half booked)
    const blockedFirst = find(
      (d) =>
        d.calendarStatus === "blocked" &&
        !d.reservationId &&
        d.dayStatus === "firstHalf"
    );
    const blockedSecond = find(
      (d) =>
        d.calendarStatus === "blocked" &&
        !d.reservationId &&
        d.dayStatus === "secondHalf"
    );
    const fullBlocked = find(
      (d) =>
        d.calendarStatus === "blocked" &&
        !d.reservationId &&
        (!d.dayStatus || d.dayStatus === "full")
    );

    if (fullBlocked && !fullBooking) {
      addOverlay(true, "#fa7475", "full", fullClip);
    } else {
      if (!bookingFirst)
        addOverlay(!!blockedFirst, "#fa7475", "firstHalf", firstHalfClip);
      if (!bookingSecond)
        addOverlay(!!blockedSecond, "#fa7475", "secondHalf", secondHalfClip);
    }

    // Special price overlays
    const specialFirst = find(
      (d) =>
        d.isSpecialPrice > 0 &&
        d.calendarStatus === "available" &&
        d.dayStatus === "firstHalf"
    );
    const specialSecond = find(
      (d) =>
        d.isSpecialPrice > 0 &&
        d.calendarStatus === "available" &&
        d.dayStatus === "secondHalf"
    );
    const fullSpecial = find(
      (d) =>
        d.isSpecialPrice > 0 &&
        d.calendarStatus === "available" &&
        (!d.dayStatus || d.dayStatus === "full")
    );

    const isFirstHalfBookedOrBlocked =
      !!bookingFirst ||
      !!find(
        (d) => d.calendarStatus === "blocked" && d.dayStatus === "firstHalf"
      );
    const isSecondHalfBookedOrBlocked =
      !!bookingSecond ||
      !!find(
        (d) => d.calendarStatus === "blocked" && d.dayStatus === "secondHalf"
      );
    const isFullBookedOrBlocked =
      !!fullBooking ||
      !!find(
        (d) =>
          d.calendarStatus === "blocked" &&
          (!d.dayStatus || d.dayStatus === "full")
      );

    if (!isFullBookedOrBlocked && fullSpecial) {
      addOverlay(true, "#30d9c9", "full", fullClip);
    } else {
      if (!isFirstHalfBookedOrBlocked)
        addOverlay(!!specialFirst, "#30d9c9", "firstHalf", firstHalfClip);
      if (!isSecondHalfBookedOrBlocked)
        addOverlay(!!specialSecond, "#30d9c9", "secondHalf", secondHalfClip);
    }

    const specialPriceEntry = find((d) => d.isSpecialPrice > 0);
    const finalPrice = specialPriceEntry?.isSpecialPrice || basePrice;

    const bookingMap = this.buildGroupedMap(
      listing?.calendarData?.blockedDates || [],
      "reservationId",
      "reservationGuestName"
    );
    const blockedMap = this.buildGroupedMap(
      listing?.calendarData?.blockedDates || [],
      "groupId",
      "guestName",
      (e) => e.calendarStatus === "blocked" && !e.reservationId
    );

    const cellWidth = 100;

    return (
      <div className={`text-center ${s.priceBox} ${s.positionRelative}`}>
        {overlays.map((overlay, i) => (
          <div
            key={i}
            className={s.overlayHalf}
            style={{
              backgroundColor: overlay.color,
              position: "absolute",
              top:
                overlay.half === "firstHalf" || overlay.half === "full"
                  ? "0"
                  : "2px",
              left: overlay.half === "secondHalf" ? "2px" : "0",
              width: overlay.half === "full" ? "100%" : "calc(100% - 2px)",
              height: overlay.half === "full" ? "100%" : "calc(100% - 2px)",
              zIndex: 2,
              clipPath: overlay.clipPath,
            }}
          />
        ))}

        {/* Booking badges */}
        {dayEntries
          .filter((entry) => {
            const key = `${entry.reservationId}_${entry.reservationGuestName}`;
            return bookingMap[key] && bookingMap[key].startDate === dateStr;
          })
          .map((entry, idx) => {
            const key = `${entry.reservationId}_${entry.reservationGuestName}`;
            const booking = bookingMap[key];
            if (!booking) return null;
            let leftOffset =
              entry.dayStatus === "secondHalf" ? cellWidth / 2 : 0;
            const widthPx = booking.totalDays * cellWidth;
            const displayName = this.getDisplayName(
              entry.reservationGuestName,
              widthPx
            );
            return (
              <OverlayTrigger
                key={`booking-${idx}`}
                placement="top"
                overlay={
                  <Tooltip>{entry.reservationGuestName || "Booked"}</Tooltip>
                }
              >
                <a href={`/users/trips/receipt/${entry.reservationId}`}>
                  <div
                    className={`${s.blockBudget} ${s.bookingBudget}`}
                    style={{
                      position: "absolute",
                      left: `${
                        widthPx <= 50 ? leftOffset + 5 : leftOffset + 15
                      }px`,
                      width: `${widthPx <= 50 ? widthPx - 10 : widthPx - 30}px`,
                      zIndex: 3,
                    }}
                  >
                    <img src={userIcon} alt="" className={s.mr2} />
                    {displayName}
                  </div>
                </a>
              </OverlayTrigger>
            );
          })}

        {/* Blocked guest badges */}
        {dayEntries
          .filter((entry) => {
            const key = `${entry.groupId}_${entry.guestName || "___EMPTY___"}`;
            return (
              blockedMap[key] &&
              blockedMap[key].startDate === dateStr &&
              !dayEntries.some(
                (e) =>
                  e.reservationId &&
                  e.calendarStatus === "blocked" &&
                  e.dayStatus === entry.dayStatus // skip only if same half is booked
              )
            );
          })
          .map((entry, idx) => {
            const key = `${entry.groupId}_${entry.guestName || "___EMPTY___"}`;
            const block = blockedMap[key];
            if (!block) return null;
            let leftOffset =
              entry.dayStatus === "secondHalf" ? cellWidth / 2 : 0;
            const widthPx = block.totalDays * cellWidth;
            const displayName = this.getDisplayName(entry.guestName, widthPx);
            return (
              <OverlayTrigger
                key={`blocked-${idx}`}
                placement="top"
                overlay={<Tooltip>{entry.guestName || "Blocked"}</Tooltip>}
              >
                <div
                  className={`${s.blockBudget} ${s.pointer}`}
                  style={{
                    position: "absolute",
                    left: `${
                      widthPx <= 50 ? leftOffset + 5 : leftOffset + 15
                    }px`,
                    width: `${widthPx <= 50 ? widthPx - 10 : widthPx - 30}px`,
                    zIndex: 3,
                    backgroundColor: "#fa7475",
                  }}
                >
                  <img src={userIcon} className={s.mr2} />
                  {displayName || "Blocked"}
                </div>
              </OverlayTrigger>
            );
          })}

        {/* Price */}
        {!hasBooking ? (
          <div
            className={`${s.zIndex1} ${s.positionRelative} ${s.basePrice} ${
              s.borderBottom
            } ${s.borderLeft} ${isLast ? s.borderEnd : ""}`}
          >
            <CurrencyConverter amount={finalPrice} from={currency} />
          </div>
        ) : (
          <div
            className={`${s.zIndex1} ${s.positionRelative} ${s.basePrice} ${
              s.borderBottom
            } ${s.borderLeft} ${isLast ? s.borderEnd : ""}`}
          />
        )}
      </div>
    );
  }
}

export default CalendarCell;
